import { KarmaImpact, StoryContext } from '../types';

interface KeywordAnalysis {
  altruisticScore: number;
  selfishScore: number;
  violentScore: number;
  peacefulScore: number;
  leadershipScore: number;
  cooperationScore: number;
  totalScore: number;
}

export class KarmaEngine {
  // Keyword dictionaries for moral analysis
  private static karmaKeywords = {
    altruistic: [
      'help', 'save', 'protect', 'heal', 'share', 'give', 'donate', 'sacrifice',
      'rescue', 'assist', 'support', 'care', 'nurture', 'defend', 'shelter',
      'guide', 'teach', 'mentor', 'comfort', 'forgive', 'mercy', 'compassion'
    ],
    selfish: [
      'steal', 'take', 'hoard', 'exploit', 'abandon', 'betray', 'cheat',
      'deceive', 'manipulate', 'use', 'profit', 'gain', 'benefit', 'advantage',
      'selfishly', 'greed', 'covet', 'ignore', 'neglect', 'refuse'
    ],
    violent: [
      'kill', 'murder', 'attack', 'assault', 'fight', 'strike', 'hit',
      'destroy', 'devastate', 'annihilate', 'harm', 'hurt', 'damage',
      'threaten', 'intimidate', 'torture', 'war', 'battle', 'crush'
    ],
    peaceful: [
      'negotiate', 'mediate', 'discuss', 'talk', 'peaceful', 'calm',
      'diplomatic', 'compromise', 'understand', 'listen', 'resolve',
      'harmony', 'balance', 'patience', 'gentle', 'kind', 'nonviolent'
    ],
    leadership: [
      'lead', 'command', 'organize', 'direct', 'coordinate', 'inspire',
      'motivate', 'rally', 'unite', 'delegate', 'strategy', 'plan',
      'decision', 'responsibility', 'authority', 'influence'
    ],
    cooperation: [
      'cooperate', 'collaborate', 'teamwork', 'together', 'unite',
      'alliance', 'partnership', 'coordinate', 'combine', 'join',
      'collective', 'group', 'community', 'shared', 'mutual'
    ]
  };

  // Location modifiers for context-aware karma
  private static locationModifiers = {
    hospital: { healing: 1.5, violence: -2.0 },
    school: { teaching: 1.3, learning: 1.2 },
    battlefield: { violence: 0.8, courage: 1.4 },
    temple: { spiritual: 1.4, theft: -2.0 },
    market: { trade: 1.2, theft: -1.5 },
    slums: { helping_poor: 1.6, exploitation: -1.8 },
    palace: { leadership: 1.3, betrayal: -1.5 }
  };

  /**
   * Calculate comprehensive karma impact from a player's choice
   */
  static calculateKarmaImpact(choice: string, context: StoryContext): KarmaImpact {
    const keywordAnalysis = this.analyzeChoiceKeywords(choice);
    const contextWeight = this.calculateContextWeight(context);
    const momentum = this.calculateKarmaMomentum(context.recentHistory);
    const locationModifier = this.getLocationModifier(context.location);

    // Calculate base karma score from keyword analysis
    let baseScore = this.calculateBaseKarmaScore(keywordAnalysis, context.cartridgeSettings.karmaWeights);
    
    // Apply context modifiers
    baseScore *= contextWeight;
    baseScore *= locationModifier;
    
    // Apply momentum (recent history influence)
    const momentumBonus = momentum * 0.1; // 10% influence from momentum
    baseScore += momentumBonus;

    // Calculate universal ripple effect (affects all players in the cartridge)
    const universalRipple = baseScore * 0.1;

    // Determine convergence weight (triggers player meetings)
    const convergenceWeight = Math.abs(baseScore) > 15 ? 1.5 : 1.0;

    // Generate narrative modifier for story generation
    const narrativeModifier = this.generateNarrativeModifier(keywordAnalysis, baseScore);

    // Generate reasoning explanation
    const reasoning = this.generateReasoning(keywordAnalysis, context, momentum);

    return {
      personalDelta: Math.round(baseScore),
      universalRipple: Math.round(universalRipple * 10) / 10,
      convergenceWeight,
      narrativeModifier,
      reasoning
    };
  }

  /**
   * Analyze choice text for moral keywords
   */
  static analyzeChoiceKeywords(choice: string): KeywordAnalysis {
    const choiceLower = choice.toLowerCase();
    const analysis: KeywordAnalysis = {
      altruisticScore: 0,
      selfishScore: 0,
      violentScore: 0,
      peacefulScore: 0,
      leadershipScore: 0,
      cooperationScore: 0,
      totalScore: 0
    };

    // Count keyword matches for each category
    Object.entries(this.karmaKeywords).forEach(([category, keywords]) => {
      const score = keywords.reduce((count, keyword) => {
        return count + (choiceLower.includes(keyword) ? 1 : 0);
      }, 0);
      
      analysis[`${category}Score` as keyof KeywordAnalysis] = score;
    });

    // Calculate weighted total score
    analysis.totalScore = 
      analysis.altruisticScore * 10 +
      analysis.peacefulScore * 8 +
      analysis.cooperationScore * 6 +
      analysis.leadershipScore * 5 -
      analysis.selfishScore * 8 -
      analysis.violentScore * 12;

    return analysis;
  }

  /**
   * Calculate karma momentum from recent player history
   */
  static calculateKarmaMomentum(recentHistory: string[]): number {
    if (!recentHistory.length) return 0;

    let momentum = 0;
    const recentChoices = recentHistory.slice(-5); // Last 5 choices

    recentChoices.forEach((choice, index) => {
      const analysis = this.analyzeChoiceKeywords(choice);
      const weight = (index + 1) / recentChoices.length; // More recent = higher weight
      momentum += analysis.totalScore * weight;
    });

    return momentum / recentChoices.length;
  }

  /**
   * Calculate context weight based on player level and situation
   */
  private static calculateContextWeight(context: StoryContext): number {
    let weight = 1.0;
    
    // Higher level players have more karma impact
    weight += (context.playerLevel - 1) * 0.05;
    
    // Ensure weight stays within reasonable bounds
    return Math.max(0.5, Math.min(2.0, weight));
  }

  /**
   * Get location-based karma modifier
   */
  private static getLocationModifier(location: string): number {
    const locationLower = location.toLowerCase();
    
    if (locationLower.includes('hospital')) {
      return 1.3; // Hospital amplifies healing actions
    } else if (locationLower.includes('battlefield')) {
      return 1.1; // Battlefield slightly amplifies all actions
    } else if (locationLower.includes('temple')) {
      return 1.2; // Temple enhances spiritual actions
    } else if (locationLower.includes('slums')) {
      return 1.4; // Slums amplify both good and bad karma
    } else if (locationLower.includes('palace')) {
      return 1.15; // Palace slightly enhances leadership
    }
    
    return 1.0; // Neutral location
  }

  /**
   * Calculate base karma score from keyword analysis
   */
  private static calculateBaseKarmaScore(
    analysis: KeywordAnalysis, 
    weights: StoryContext['cartridgeSettings']['karmaWeights']
  ): number {
    return (
      analysis.altruisticScore * weights.altruistic * 10 +
      analysis.peacefulScore * weights.peaceful * 8 +
      analysis.cooperationScore * weights.cooperation * 6 +
      analysis.leadershipScore * weights.leadership * 5 +
      analysis.selfishScore * weights.selfish * 8 +
      analysis.violentScore * weights.violent * 12
    );
  }

  /**
   * Generate narrative modifier for story generation
   */
  private static generateNarrativeModifier(analysis: KeywordAnalysis, karmaScore: number): string {
    if (karmaScore > 20) {
      return 'The universe resonates with your noble actions, creating ripples of positive energy.';
    } else if (karmaScore > 10) {
      return 'Your helpful nature influences the world around you in subtle but meaningful ways.';
    } else if (karmaScore > 0) {
      return 'Your actions contribute to a slightly more harmonious reality.';
    } else if (karmaScore > -10) {
      return 'The world remains indifferent to your neutral choices.';
    } else if (karmaScore > -20) {
      return 'Your actions create small disturbances in the cosmic balance.';
    } else {
      return 'Dark energy spreads from your choices, corrupting the fabric of reality.';
    }
  }

  /**
   * Generate reasoning explanation for karma change
   */
  private static generateReasoning(
    analysis: KeywordAnalysis, 
    context: StoryContext, 
    momentum: number
  ): string {
    const reasons: string[] = [];

    if (analysis.altruisticScore > 0) {
      reasons.push(`altruistic actions (+${analysis.altruisticScore * 10})`);
    }
    if (analysis.selfishScore > 0) {
      reasons.push(`selfish behavior (-${analysis.selfishScore * 8})`);
    }
    if (analysis.violentScore > 0) {
      reasons.push(`violent actions (-${analysis.violentScore * 12})`);
    }
    if (analysis.peacefulScore > 0) {
      reasons.push(`peaceful approach (+${analysis.peacefulScore * 8})`);
    }
    if (analysis.cooperationScore > 0) {
      reasons.push(`cooperative spirit (+${analysis.cooperationScore * 6})`);
    }
    if (analysis.leadershipScore > 0) {
      reasons.push(`leadership qualities (+${analysis.leadershipScore * 5})`);
    }

    if (momentum > 5) {
      reasons.push('positive momentum amplifies your choices');
    } else if (momentum < -5) {
      reasons.push('negative spiral intensifies consequences');
    }

    const locationEffect = this.getLocationModifier(context.location);
    if (locationEffect > 1.1) {
      reasons.push(`${context.location} amplifies your karma`);
    } else if (locationEffect < 0.9) {
      reasons.push(`${context.location} dampens the impact`);
    }

    return reasons.length > 0 
      ? reasons.join(', ')
      : 'Your choice creates subtle shifts in the karmic flow';
  }
} 